export async function initQuickReorder(root) {
  const config = window.WISHLIST_CONFIG;
  const buttons = Array.from(root.querySelectorAll("[data-reorder-variant]"));
  if (!buttons.length || !config?.storefrontToken) return;
  const groups = new Map();
  const pending = new Set();
  for (const button of buttons) {
    button.hidden = true;
    button.disabled = true;
    const gid = button.getAttribute("data-reorder-variant");
    if (!/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(gid || "")) continue;
    if (!groups.has(gid)) groups.set(gid, []);
    groups.get(gid).push(button);
  }
  async function refresh(ids) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    if (timer.unref) timer.unref();
    let nodes = [];
    try {
      const response = await fetch(`https://${config.storefrontDomain}/api/${config.storefrontApiVersion}/graphql.json`, {
        method: "POST", signal: controller.signal,
        headers: { "content-type": "application/json", "X-Shopify-Storefront-Access-Token": config.storefrontToken },
        body: JSON.stringify({ query: `query QuickReorder($ids: [ID!]!) {
          nodes(ids: $ids) { ... on ProductVariant {
            id availableForSale currentlyNotInStock price { amount currencyCode } product { onlineStoreUrl }
          } }
        }`, variables: { ids } }),
      });
      const body = response.ok ? await response.json() : null;
      if (Array.isArray(body?.data?.nodes) && !body.errors) nodes = body.data.nodes;
    } catch (_) { /* No verified stock means no reorder button. */ }
    finally { clearTimeout(timer); }
    const available = new Set();
    for (const gid of ids) {
      const node = nodes.find((entry) => entry?.id === gid);
      const inStock = node?.availableForSale === true && node.currentlyNotInStock === false &&
        !!node.product?.onlineStoreUrl && !!node.price;
      if (inStock) available.add(gid);
      for (const button of groups.get(gid) || []) {
        button.hidden = !inStock;
        button.disabled = !inStock || pending.has(gid);
        if (inStock) {
          const price = new Intl.NumberFormat(undefined, { style: "currency", currency: node.price.currencyCode }).format(Number(node.price.amount));
          button.textContent = `${button.getAttribute("data-reorder-label") || "Buy again"} · ${price}`;
        }
      }
    }
    return available;
  }
  for (const [gid, controls] of groups) {
    for (const button of controls) {
      if (button.dataset.reorderInitialized) continue;
      button.dataset.reorderInitialized = "true";
      button.addEventListener("click", async () => {
        if (button.hidden || button.disabled || pending.has(gid)) return;
        pending.add(gid);
        controls.forEach((control) => { control.disabled = true; });
        const status = button.parentElement.querySelector("[data-reorder-status]");
        const announce = (message) => { status.hidden = false; status.textContent = message; };
        announce("Checking stock…");
        try {
          const available = await refresh([gid]);
          if (!available.has(gid)) { announce("This copy is no longer available to reorder."); return; }
          announce("Adding to cart…");
          let timer;
          let result;
          try {
            result = await Promise.race([
              window.WISHLIST?.addToCart(gid.split("/").pop()),
              new Promise((resolve) => { timer = setTimeout(() => resolve(null), 10000); }),
            ]);
          } finally { clearTimeout(timer); }
          if (!result) {
            controls.forEach((control) => { control.hidden = true; });
            announce("Couldn't confirm the addition. Check your cart before trying again.");
            return;
          }
          announce("Added to cart.");
          await refresh([gid]);
        } catch (_) {
          controls.forEach((control) => { control.hidden = true; });
          announce("Couldn't confirm the addition. Check your cart before trying again.");
        } finally {
          pending.delete(gid);
          controls.forEach((control) => { control.disabled = control.hidden; });
        }
      });
    }
  }
  const ids = [...groups.keys()];
  for (let i = 0; i < ids.length; i += 25) await refresh(ids.slice(i, i + 25));
}

if (typeof document !== "undefined") {
  initQuickReorder(document);
  document.addEventListener("mycaptcan:replace", () => initQuickReorder(document));
  window.addEventListener("pageshow", (event) => { if (event.persisted) initQuickReorder(document); });
}
